/**
 * Voci della navigazione primaria dell'app autenticata — funzione PURA.
 *
 * Fonte unica: header.js (desktop) e menu.js (drawer mobile) dichiarano oggi
 * le stesse voci con gating duplicato a mano; la shell v2 legge da qui.
 * Fase 2 (post-flip): migrare anche menu.js su questo config.
 *
 * Voci, ordine e gating IDENTICI a header.js: i funnel misurati (eventi
 * calendar_nav_clicked, gate Maturità per discoverability) non vanno alterati
 * insieme al cambio di layout, altrimenti i dati pre/post non sono confrontabili.
 *
 * Nessun import pesante qui (niente lucide/redux): il file resta testabile
 * come funzione pura. Le icone si risolvono da `iconId` in navConfig.js.
 */

/**
 * Home di ciascuna vista. Fonte unica: la usano lo switch (dove porta la
 * scelta), la sincronizzazione vista↔rotta (quale vista descrive l'URL
 * corrente) e il guard di rotta. Tre copie a mano erano tre modi di
 * divergere.
 */
export const ROLE_HOME_PATHS = {
  student: '/home',
  parent: '/home/dashboard-genitori',
  teacher: '/home/dashboard-professori',
};

/**
 * Chi possiede un ruolo, letto dal documento utente. Stessa definizione di
 * navConfig.js (`is_parent_account || role === 'parent'`, `role === 'teacher'`)
 * e delle due pagine dashboard, in un posto solo: il guard di rotta e la nav
 * devono rispondere la stessa cosa, altrimenti il guard rimbalza chi la
 * sidebar considera avente diritto.
 * `student` è di tutti: nessuno resta senza una vista.
 */
export const ownsRole = (user, role) => {
  if (role === 'student') return true;
  if (role === 'parent')
    return !!(user?.is_parent_account || user?.role === 'parent');
  if (role === 'teacher') return user?.role === 'teacher';
  return false;
};

/**
 * La vista che l'URL corrente sta già mostrando, o null se la rotta è
 * condivisa fra le viste (`/home`, `/home/materiale`, ...). `/home` NON
 * mappa su `student`: è la home di tutti e resetterebbe la vista a ogni
 * passaggio.
 */
export const viewRoleForPath = (pathname) => {
  if (typeof pathname !== 'string') return null;
  // via la query/hash e lo slash finale: /home/dashboard-genitori/ è la stessa rotta
  const clean = pathname.split('?')[0].split('#')[0].replace(/\/+$/, '');
  if (clean === ROLE_HOME_PATHS.parent) return 'parent';
  if (clean === ROLE_HOME_PATHS.teacher) return 'teacher';
  return null;
};

/**
 * viewRole (opzionale) — switch di vista dal footer sidebar: chi HA il ruolo
 * può guardare l'app con la nav di un'altra vista (es. un professore che
 * vuole la vista studente). Una viewRole NON posseduta viene ignorata e
 * vale il ruolo reale: il gating di sicurezza resta nelle pagine.
 */
export const resolveEffectiveRole = ({ isParent, isTeacher, viewRole }) => {
  if (viewRole === 'student') return 'student';
  if (viewRole === 'parent' && isParent) return 'parent';
  if (viewRole === 'teacher' && isTeacher) return 'teacher';
  if (isParent) return 'parent';
  if (isTeacher) return 'teacher';
  return 'student';
};

export const buildNavItems = ({
  isParent,
  isTeacher,
  showMaturita,
  viewRole = null,
}) => {
  const effectiveRole = resolveEffectiveRole({ isParent, isTeacher, viewRole });
  const base = [
    {
      id: 'home',
      to: '/home',
      end: true,
      iconId: 'home',
      labelKey: 'header.home',
      labelDefault: 'Home',
    },
    {
      id: 'review',
      to: '/home/materiale',
      end: true,
      iconId: 'library',
      labelKey: 'header.review',
      labelDefault: 'Ripasso',
    },
    {
      id: 'oralExams',
      to: '/home/ripeti',
      end: true,
      iconId: 'sparkles',
      labelKey: 'header.oralExams',
      labelDefault: 'Esami Orali',
    },
  ];

  if (effectiveRole === 'parent') {
    return [
      ...base,
      {
        id: 'parentDashboard',
        to: '/home/dashboard-genitori',
        end: true,
        iconId: 'users',
        labelKey: 'header.parentDashboard',
        labelDefault: 'Dashboard Genitori',
      },
    ];
  }

  if (effectiveRole === 'teacher') {
    return [
      ...base,
      {
        id: 'calendar',
        to: '/home/calendario',
        end: true,
        iconId: 'calendar',
        labelKey: 'header.calendar',
        labelDefault: 'Calendario',
      },
      {
        id: 'teacherDashboard',
        to: '/home/dashboard-professori',
        end: true,
        iconId: 'users',
        labelKey: 'header.dashboard',
        labelDefault: 'Dashboard',
      },
    ];
  }

  return [
    ...base,
    ...(showMaturita
      ? [
          {
            id: 'maturita',
            to: '/home/maturita-esame',
            end: true,
            iconId: 'graduationCap',
            labelKey: 'header.maturita',
            labelDefault: 'Maturità',
          },
        ]
      : []),
    {
      id: 'progress',
      to: '/home/progressi',
      end: true,
      iconId: 'trendingUp',
      labelKey: 'header.progress',
      labelDefault: 'Progressi',
    },
    {
      id: 'calendar',
      to: '/home/calendario',
      end: true,
      iconId: 'calendar',
      labelKey: 'header.calendar',
      labelDefault: 'Calendario',
    },
    {
      // ULTIMA voce (CEO 26/08). Le voci scendono da "quello che faccio" a
      // "quello che guardo": Home/Ripasso/Esami Orali sono il lavoro,
      // Progressi/Calendario sono il mio lavoro misurato e pianificato,
      // la Classifica e' l'unica che parla degli altri e si guarda ogni
      // tanto. Prima stava accanto a Progressi (CEO 25/08, per farla
      // scoprire): scoperta ottenuta, ora torna in fondo.
      // Solo vista studente: genitori e docenti non gareggiano.
      id: 'ranking',
      to: '/home/classifica',
      end: true,
      iconId: 'trophy',
      labelKey: 'header.ranking',
      labelDefault: 'Classifica',
    },
  ];
};
